// IIFE guards top-level consts from colliding with the other two quiz scripts.
// Narrow exception to the "no price formatting in JS" rule: only the bundle
// TOTAL is client-formatted; individual slot prices are server-rendered.
(() => {
// Same output as snippets/price-display.liquid; the prefix comes from the shop's money format.
const formatMoney = (cents, moneyPrefix) =>
  `${moneyPrefix}${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\.00$/, '')}`;

const ANSWERS_STORAGE_KEY = 'themex-quiz-answers';
const STEPS = ['cleanse', 'treat', 'seal'];

// Cleanse/Treat read wash-freq; Seal reads heat-styling. Missing keys blank the line.
const MODIFIER_COPY = {
  cleanse: {
    'every-day': 'Daily washing calls for a gentle formula that doesn’t strip.',
    'every-2-3-days': 'A balanced cadence — this cleanser keeps things comfortable between washes.',
    'twice-a-week': 'Twice-weekly washes work best with a nourishing cleanser.',
    'once-a-week': 'Weekly washes need a deeper clean without over-drying.',
  },
  treat: {
    'every-day': 'Daily use of this treatment locks moisture in between washes.',
    'every-2-3-days': 'A mid-week treatment reinforces your hair between cleanses.',
    'twice-a-week': 'Apply this treatment on every wash for consistent results.',
    'once-a-week': 'One weekly treatment is enough to keep results compounding.',
  },
  seal: {
    'every-day': 'Daily heat means daily protection — this seals in what the routine builds.',
    'few-times-a-week': 'Heat a few times a week? This seals the cuticle and adds shine before styling.',
    'rarely': 'Even without regular heat, this seal locks in moisture and softness.',
    'never': 'Air-dry only — this seal is your finishing touch for definition and shine.',
  },
};

class QuizResultsProducts extends HTMLElement {
  constructor() {
    super();
    // handles = [primaryHandle, altHandle]; swap is an index flip.
    this.slots = new Map();
    this.discountCents = 0;
    this.openSlotId = null;
    this.matrix = null;
    // Separate caches — same handle has different DOM as primary vs alt.
    this.primaryCache = new Map(); // handle → [data-quiz-slot-fragment] element
    this.altCache = new Map();     // handle → [data-quiz-alt-fragment] element
    this.answers = null;
  }

  connectedCallback() {
    this.discountCents = Number(this.dataset.discountCents) || 0;
    [this.moneyPrefix = ''] = (this.dataset.moneyFormat ?? '').split('|');

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-slot-trigger]');
      if (trigger) this._toggleTray(trigger.dataset.slotId);
    });

    this.addEventListener('click', (event) => {
      const chooseButton = event.target.closest('[data-alt-choose]:not([disabled])');
      if (chooseButton) this._chooseAlternative(chooseButton);
    });

    this.addEventListener('click', (event) => {
      const bundleButton = event.target.closest('[data-bundle-cta]');
      if (bundleButton) this._addBundle(bundleButton);
    });

    this._hydrate();
  }

  async _hydrate() {
    this.answers = this._readAnswers();
    if (!this.answers) {
      this._renderEmpty();
      return;
    }

    try {
      this.matrix = await this._loadMatrix();
    } catch (error) {
      console.warn('[QuizResultsProducts] failed to load matrix:', error);
      this._renderEmpty();
      return;
    }

    const row = this._matchRow(this.answers, this.matrix);
    if (!row) {
      console.warn('[QuizResultsProducts] no matrix row for answers', this.answers);
      this._renderEmpty();
      return;
    }

    // Fetch every handle in BOTH variants up-front so swap is instant.
    const primaryHandles = new Set();
    const altHandles = new Set();
    for (const step of STEPS) {
      const [p, a] = row[step] || [];
      if (p) { primaryHandles.add(p); altHandles.add(p); } // swap can put it in the tray
      if (a) { primaryHandles.add(a); altHandles.add(a); } // swap can promote it to primary
    }
    await Promise.all([
      ...[...primaryHandles].map((h) => this._fetchFragment(h, 'primary')),
      ...[...altHandles].map((h) => this._fetchFragment(h, 'alt')),
    ]);

    for (const step of STEPS) {
      const handles = row[step] || [];
      this._hydrateSlot(step, handles);
    }

    this._applyModifierCopy();
    this._updateBundlePrice();
  }

  _readAnswers() {
    try {
      const raw = localStorage.getItem(ANSWERS_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      // Minimum requirement: the four driver keys must be present.
      if (!parsed.concern?.optionKey || !parsed.texture?.optionKey || !parsed.treatment?.optionKey) return null;
      const hasScalp = parsed['scalp-type']?.optionKey || parsed['scalp-issue']?.optionKey;
      if (!hasScalp) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  async _loadMatrix() {
    // Source of truth: the metaobject-driven <script data-quiz-matrix> tag in the snippet.
    const tag = this.querySelector('script[data-quiz-matrix]');
    if (!tag) throw new Error('quiz matrix script tag missing');
    const rows = JSON.parse(tag.textContent);
    return { version: 1, rows };
  }

  _matchRow(answers, matrix) {
    if (!matrix?.rows) return null;
    const concern = answers.concern.optionKey;
    const texture = answers.texture.optionKey;
    const treatment = answers.treatment.optionKey;
    const scalp = concern === 'scalp' ? answers['scalp-issue']?.optionKey : answers['scalp-type']?.optionKey;
    if (!scalp) return null;
    return matrix.rows.find(
      (r) => r.concern === concern && r.texture === texture && r.treatment === treatment && r.scalp === scalp,
    );
  }

  // variant: 'primary' = ?view=quiz-card ; 'alt' = ?view=quiz-alt-card.
  async _fetchFragment(handle, variant) {
    const cache = variant === 'alt' ? this.altCache : this.primaryCache;
    const selector = variant === 'alt' ? '[data-quiz-alt-fragment]' : '[data-quiz-slot-fragment]';
    const view = variant === 'alt' ? 'quiz-alt-card' : 'quiz-card';
    if (cache.has(handle)) return cache.get(handle);
    const url = `/products/${encodeURIComponent(handle)}?view=${view}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`fragment ${res.status} for ${handle}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector(selector);
      cache.set(handle, fragment);
      return fragment;
    } catch (error) {
      console.warn(`[QuizResultsProducts] ${variant} fragment fetch failed for '${handle}':`, error);
      cache.set(handle, null);
      return null;
    }
  }

  _hydrateSlot(step, handles) {
    const primaryHandle = handles[0] ?? null;
    const altHandle = handles[1] ?? null;
    const wrap = this.querySelector(`[data-quiz-slot-wrap="${step}"]`);
    const slotCard = this.querySelector(`[data-slot-card="${step}"]`);
    if (!wrap || !slotCard) return;

    // Ordered pair; swap is an index toggle.
    this.slots.set(step, { handles: [primaryHandle, altHandle], activeIndex: 0, priceCents: 0, mrpCents: 0, variantId: '', available: false });

    this._paintSlotCard(step, primaryHandle);

    const triggers = wrap.querySelectorAll('[data-slot-trigger]');
    const tray = this.querySelector(`[data-tray="${step}"]`);

    // Tray element always stays in the DOM (owns its grid cell); trigger/content toggles.
    if (altHandle) {
      triggers.forEach((t) => { t.hidden = false; });
      this._paintAltCard(step, altHandle);
    } else {
      triggers.forEach((t) => { t.hidden = true; });
    }
  }

  _paintSlotCard(step, handle) {
    const slotCard = this.querySelector(`[data-slot-card="${step}"]`);
    if (!slotCard) return;
    const fragment = handle ? this.primaryCache.get(handle) : null;
    const state = this.slots.get(step);

    if (!fragment) {
      const unavailable = this.querySelector('[data-unavailable-template]');
      if (unavailable) slotCard.replaceChildren(unavailable.content.cloneNode(true));
      slotCard.dataset.slotPrice = '0';
      slotCard.dataset.slotVariantId = '';
      slotCard.dataset.slotAvailable = 'false';
      if (state) Object.assign(state, { priceCents: 0, mrpCents: 0, variantId: '', available: false });
      return;
    }

    // Propagate fragment's price/variant/available metadata onto the slot card.
    const clone = fragment.cloneNode(true);
    slotCard.replaceChildren(...clone.childNodes);
    slotCard.classList.remove('opacity-0');
    const priceCents = Number(clone.dataset.price) || 0;
    const mrpCents = Number(clone.dataset.comparePrice) || priceCents;
    const variantId = clone.dataset.variantId || '';
    const available = clone.dataset.available === 'true';
    slotCard.dataset.slotPrice = String(priceCents);
    slotCard.dataset.slotVariantId = variantId;
    slotCard.dataset.slotAvailable = String(available);
    if (state) Object.assign(state, { priceCents, mrpCents, variantId, available });
  }

  _paintAltCard(step, handle) {
    const container = this.querySelector(`[data-tray-content="${step}"]`);
    const template = this.querySelector('[data-alt-card-template]');
    if (!container || !template) return;

    const node = template.content.cloneNode(true);
    const body = node.querySelector('[data-alt-card-body]');
    if (!body) return;

    const fragment = handle ? this.altCache.get(handle) : null;
    if (fragment) {
      // Fragment already includes the Choose button; tag it with the target slot id.
      const clone = fragment.cloneNode(true);
      body.replaceChildren(...clone.childNodes);
      const chooseBtn = body.querySelector('[data-alt-choose]');
      if (chooseBtn) {
        chooseBtn.dataset.targetSlot = step;
        if (fragment.dataset.available !== 'true') {
          chooseBtn.disabled = true;
          chooseBtn.setAttribute('aria-disabled', 'true');
        }
      }
    } else {
      const unavailable = this.querySelector('[data-unavailable-template]');
      if (unavailable) body.replaceChildren(unavailable.content.cloneNode(true));
    }

    container.replaceChildren(node);
  }

  _applyModifierCopy() {
    if (!this.answers) return;
    for (const step of STEPS) {
      const el = this.querySelector(`[data-quiz-slot-wrap="${step}"] [data-slot-modifier-copy]`);
      if (!el) continue;
      const modifierKey = step === 'seal' ? this.answers['heat-styling']?.optionKey : this.answers['wash-freq']?.optionKey;
      const copy = MODIFIER_COPY[step]?.[modifierKey] || '';
      el.textContent = copy;
    }
  }

  _renderEmpty() {
    const empty = this.querySelector('[data-quiz-empty]');
    const grid = this.querySelector('[data-quiz-slots]');
    const trays = this.querySelector('[data-quiz-trays]');
    const cta = this.querySelector('[data-bundle-cta]');
    if (empty) empty.classList.remove('hidden');
    if (grid) grid.classList.add('hidden');
    if (trays) trays.classList.add('hidden');
    if (cta) {
      cta.disabled = true;
      cta.classList.add('hidden');
    }
  }

  // Open state = data-open attribute; CSS in the snippet handles the grid-rows animation.
  _openTray(tray, triggers) {
    tray.setAttribute('data-open', '');
    tray.setAttribute('aria-hidden', 'false');
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    setTimeout(() => tray.scrollIntoView({ behavior: 'smooth', block: 'start' }), 320);
  }

  _closeTray(tray, triggers) {
    tray.removeAttribute('data-open');
    tray.setAttribute('aria-hidden', 'true');
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  }

  _toggleTray(slotId) {
    const target = this.querySelector(`[data-tray="${slotId}"]`);
    if (!target) return;
    // Only slots with an alt during hydration have content to reveal.
    const hasContent = this.slots.get(slotId)?.handles?.[1];
    if (!hasContent) return;

    const triggers = [...this.querySelectorAll(`[data-slot-trigger][data-slot-id="${slotId}"]`)];

    if (this.openSlotId === slotId) {
      this._closeTray(target, triggers);
      this.openSlotId = null;
      return;
    }

    this._closeOpenTray();
    this._openTray(target, triggers);
    this.openSlotId = slotId;
  }

  _closeOpenTray() {
    if (!this.openSlotId) return;
    const previousTray = this.querySelector(`[data-tray="${this.openSlotId}"]`);
    const previousTriggers = [...this.querySelectorAll(`[data-slot-trigger][data-slot-id="${this.openSlotId}"]`)];
    if (previousTray) this._closeTray(previousTray, previousTriggers);
    this.openSlotId = null;
  }

  _chooseAlternative(button) {
    const step = button.dataset.targetSlot;
    const state = this.slots.get(step);
    if (!state || !state.handles[1]) return;

    // Flip primary <-> alt from the pre-fetched cache; no network.
    const [primary, alt] = state.handles;
    state.handles = [alt, primary];

    this._paintSlotCard(step, state.handles[0]);
    this._paintAltCard(step, state.handles[1]);

    this._closeOpenTray();
    this._updateBundlePrice();
  }

  _updateBundlePrice() {
    let totalCents = 0;
    let mrpCents = 0;
    let allAvailable = true;
    let hasAnySlot = false;

    for (const state of this.slots.values()) {
      hasAnySlot = true;
      totalCents += state.priceCents;
      mrpCents += state.mrpCents ?? state.priceCents;
      if (!state.available) allAvailable = false;
    }

    const saleCents = Math.max(0, totalCents - this.discountCents);
    const priceEl = this.querySelector('[data-bundle-price]');
    const compareEl = this.querySelector('[data-bundle-compare-price]');
    const bundleButton = this.querySelector('[data-bundle-cta]');

    if (priceEl) priceEl.textContent = totalCents > 0 ? formatMoney(saleCents, this.moneyPrefix) : '—';
    // Struck through: the summed MRP of the three products.
    if (compareEl) {
      compareEl.textContent = formatMoney(mrpCents, this.moneyPrefix);
      compareEl.classList.toggle('hidden', saleCents >= mrpCents || totalCents === 0);
    }

    if (bundleButton) {
      const canBundle = hasAnySlot && allAvailable && totalCents > 0;
      bundleButton.disabled = !canBundle;
      bundleButton.setAttribute('aria-disabled', String(!canBundle));
    }
  }

  async _addBundle(button) {
    if (button.disabled) return;

    const items = [...this.slots.values()]
      .filter((s) => s.variantId && s.available)
      .map(({ variantId }) => ({ id: variantId, quantity: 1 }));
    if (!items.length) return;

    const cartDrawer = document.querySelector('cart-drawer');

    if (!cartDrawer) {
      button.disabled = true;
      try {
        await CartAPI.addMultiple(items);
      } catch (error) {
        console.error('[QuizResultsProducts] add failed:', error);
      } finally {
        button.disabled = false;
      }
      return;
    }

    cartDrawer.open();
    await cartDrawer.addItems(items, button);
  }
}

if (!customElements.get('quiz-results-products')) {
  customElements.define('quiz-results-products', QuizResultsProducts);
}
})();
